import React, { useState, useEffect, useRef, useCallback } from "react";
import { parseAddress, assembleAddress, addressUnitConflict } from "@/lib/utils";
import { api } from "@/lib/api";

const USPS_DEBOUNCE_MS = 600;
const DEFAULT_TTL_MS = 15 * 60 * 1000;

function addressesMatch(a, b) {
  return (
    (a.street || "").trim().toUpperCase() === (b.street || "").trim().toUpperCase() &&
    (a.apt || "").trim().toUpperCase() === (b.apt || "").trim().toUpperCase() &&
    (a.city || "").trim().toUpperCase() === (b.city || "").trim().toUpperCase() &&
    (a.state || "").trim().toUpperCase() === (b.state || "").trim().toUpperCase() &&
    (a.zip || "").trim() === (b.zip || "").trim()
  );
}

function formatSuggestion(s) {
  if (!s) return "";
  return assembleAddress(s);
}

function readyToCheck(f) {
  return !addressUnitConflict(f) && !!(f.street || "").trim() && (f.zip || "").replace(/\D/g, "").length >= 5;
}

export default function AddressFields({ value, onChange, readOnly = false, compact = false, onVerification, allowGoogleCheck = true }) {
  const verifyRef = useRef(onVerification);
  verifyRef.current = onVerification;
  const notifyVerification = (t) => { if (verifyRef.current) verifyRef.current(t || null); };
  const [fields, setFields] = useState(() => parseAddress(value || ""));
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(false);
  const [result, setResult] = useState(null); // transient, memory only
  const [checking, setChecking] = useState(false);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const expiryRef = useRef(null);
  const reqRef = useRef(0);
  const mountedRef = useRef(true);

  const googleActive = allowGoogleCheck && !!config?.google_active;

  // Invalidate anything in flight and drop any displayed result.
  const clearAll = useCallback(() => {
    reqRef.current += 1;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (expiryRef.current) clearTimeout(expiryRef.current);
    if (abortRef.current) abortRef.current.abort();
    debounceRef.current = null;
    expiryRef.current = null;
    abortRef.current = null;
    setResult(null);
    setChecking(false);
    if (verifyRef.current) verifyRef.current(null);
  }, []);

  // Disabling the workflow also invalidates any pending check or receipt.
  useEffect(() => {
    if (!allowGoogleCheck) clearAll();
  }, [allowGoogleCheck, clearAll]);

  useEffect(() => {
    mountedRef.current = true;
    let live = true;
    if (!readOnly) {
      Promise.resolve(api.get("/address-validation/config"))
        .then((res) => { if (live && mountedRef.current && res?.data) setConfig(res.data); })
        .catch(() => { if (live && mountedRef.current) setConfigError(true); });
    }
    return () => {
      live = false;
      mountedRef.current = false;
      reqRef.current += 1;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (expiryRef.current) clearTimeout(expiryRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [readOnly]);

  useEffect(() => {
    if ((value || "") !== assembleAddress(fields)) {
      setFields(parseAddress(value || ""));
      clearAll();
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  function storeResult(next, expiresAt) {
    if (expiryRef.current) clearTimeout(expiryRef.current);
    const parsed = Date.parse(expiresAt || "");
    const ms = Number.isNaN(parsed) ? DEFAULT_TTL_MS : Math.min(DEFAULT_TTL_MS, Math.max(0, parsed - Date.now()));
    expiryRef.current = setTimeout(() => {
      expiryRef.current = null;
      if (mountedRef.current) { setResult(null); notifyVerification(null); }
    }, ms);
    setResult({ ...next, expiresAt: new Date(Date.now() + ms).toISOString() });
  }

  function interpret(data, current) {
    const sug = data.suggested_correction || (data.ok && data.address ? data.address : null);
    const differs = sug && !addressesMatch(current, { ...sug, apt: sug.apt || current.apt });
    return { sug, differs };
  }

  const lookupUsps = useCallback((currentFields) => {
    clearAll();
    if (!readyToCheck(currentFields)) return;
    const id = reqRef.current;
    debounceRef.current = setTimeout(async () => {
      debounceRef.current = null;
      const controller = new AbortController();
      abortRef.current = controller;
      setResult({ mode: "usps", status: "loading" });
      try {
        const res = await api.post(
          "/standardize-address",
          {
            street: currentFields.street,
            apt: currentFields.apt,
            city: currentFields.city,
            state: currentFields.state,
            zip: currentFields.zip,
            consent_google: false,
          },
          { signal: controller.signal }
        );
        if (!mountedRef.current || id !== reqRef.current) return;
        const data = res.data || {};
        if (!data.ok) {
          storeResult({ mode: "usps", status: "not_found", message: data.message }, data.expires_at);
          return;
        }
        const suggested = data.address;
        if (!suggested || addressesMatch(currentFields, suggested)) {
          storeResult({ mode: "usps", status: "matched" }, data.expires_at);
        } else {
          storeResult({ mode: "usps", status: "suggestion", suggestion: suggested }, data.expires_at);
        }
      } catch (err) {
        if (!mountedRef.current || id !== reqRef.current) return;
        storeResult({ mode: "usps", status: "not_found", message: "The address service is unavailable. You can still save it as entered." });
      }
    }, USPS_DEBOUNCE_MS);
  }, [clearAll]); // eslint-disable-line react-hooks/exhaustive-deps

  async function runGoogleCheck() {
    if (!googleActive || readOnly || checking || !readyToCheck(fields)) return;
    clearAll();
    const id = reqRef.current;
    const current = fields;
    const controller = new AbortController();
    abortRef.current = controller;
    setChecking(true);
    try {
      const res = await api.post(
        "/standardize-address",
        {
          street: current.street,
          apt: current.apt,
          city: current.city,
          state: current.state,
          zip: current.zip,
          consent_google: true,
        },
        { signal: controller.signal }
      );
      if (!mountedRef.current || id !== reqRef.current) return;
      const data = res.data || {};
      const { sug, differs } = interpret(data, current);
      const status = data.status || (data.ok ? "checked" : "not_checked");
      setChecking(false);
      if (status === "checked" && !differs) {
        const token = data.verification_token || null;
        storeResult({ mode: "google", status: "checked", message: data.message, verified: !!token }, data.expires_at);
        if (token) notifyVerification(token);
      } else if (status === "needs_review") {
        storeResult({
          mode: "google",
          token: data.suggestion_confirmed ? (data.verification_token || null) : null,
          status: "needs_review",
          message: data.message,
          reason: data.reason,
          suggestion: sug,
          confirmed: !!data.suggestion_confirmed,
        }, data.expires_at);
      } else {
        storeResult({ mode: "google", status: "not_checked", message: data.message }, data.expires_at);
      }
    } catch (err) {
      if (!mountedRef.current || id !== reqRef.current) return;
      setChecking(false);
      setResult({ mode: "google", status: "error", message: "The address check could not be completed. You can still save the address as entered." });
    }
  }

  function handleChange(key, val) {
    const next = { ...fields, [key]: val };
    setFields(next);
    onChange(assembleAddress(next));
    clearAll();
  }

  function handleBlur(e) {
    if (readOnly) return;
    if (addressUnitConflict(fields)) return;
    // Clicking Google check/accept must not erase the Google result.
    if (e?.relatedTarget?.closest?.("[data-address-guard]")) return;
    if (!config) return;
    lookupUsps(fields);
  }

  function acceptSuggestion() {
    const s = result?.suggestion;
    if (!s) return;
    if (result.mode === "google" && !allowGoogleCheck) return;
    if (result.mode === "google" && Date.parse(result.expiresAt || "") <= Date.now()) {
      clearAll();
      return;
    }
    const mode = result.mode;
    const expiresAt = result.expiresAt;
    const next = {
      street: s.street || fields.street,
      // Never clear a unit the user typed when the provider omits it.
      apt: s.apt || fields.apt || "",
      city: s.city || fields.city,
      state: s.state || fields.state,
      zip: s.zip || fields.zip,
    };
    if (addressUnitConflict(next)) {
      clearAll();
      setResult({ mode: "usps", status: "not_found", message: "The suggested street and unit have conflicting unit numbers. Please correct them before saving." });
      return;
    }
    const token = mode === "google" ? result.token : null;
    clearAll();
    setFields(next);
    onChange(assembleAddress(next));
    if (mode === "usps") setResult({ mode: "usps", status: "matched" });
    else {
      storeResult({ mode: "google", status: "updated", verified: !!token }, expiresAt);
      if (token) notifyVerification(token);
    }
  }

  function dismissSuggestion() {
    notifyVerification(null);
    if (result?.mode === "google") setResult({ mode: "google", status: "kept" });
    else setResult(null);
  }

  const labelClass = compact
    ? "text-xs uppercase tracking-wider font-semibold mb-1.5 block"
    : "block text-sm font-medium mb-1.5";
  const labelStyle = compact ? { color: "#8B918B" } : {};
  const inputClass = compact
    ? "w-full px-3.5 py-2.5 rounded-lg border outline-none focus:border-[#052F66] transition-colors"
    : "w-full px-4 py-3 rounded-lg border-2 outline-none focus:border-[#052F66] text-base";
  const inputStyle = (ro) => ({
    borderColor: "var(--fb-border)",
    background: ro ? "var(--fb-bg)" : "#FAFAF8",
    cursor: ro ? "default" : undefined,
  });

  const requiredMark = <span style={{ color: "var(--fb-error)" }}> *</span>;
  const optionalMark = (
    <span style={{ color: "var(--fb-text-muted)", fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: compact ? "0.7rem" : "0.8rem" }}>
      {" (optional)"}
    </span>
  );

  const muted = { color: "var(--fb-text-muted)" };
  const suggestion = result?.suggestion || null;
  const canUse = !!suggestion && (result.mode === "usps" || (result.confirmed && !!result.token));
  const exhausted = config && typeof config.daily_remaining === "number" && config.daily_remaining <= 0;

  return (
    <div className="space-y-3" data-testid="address-fields">
      <div>
        <label className={labelClass} style={labelStyle}>
          Street Address{!readOnly && requiredMark}
        </label>
        <input data-testid="address-street" value={fields.street}
          onChange={(e) => handleChange("street", e.target.value)} onBlur={handleBlur}
          readOnly={readOnly} placeholder="123 Main St" className={inputClass} style={inputStyle(readOnly)} />
      </div>

      <div>
        <label className={labelClass} style={labelStyle}>
          Apt / Unit{optionalMark}
        </label>
        <input data-testid="address-apt" value={fields.apt}
          onChange={(e) => handleChange("apt", e.target.value)} onBlur={handleBlur}
          readOnly={readOnly} placeholder="Apt 4, Unit B, etc." className={inputClass} style={inputStyle(readOnly)} />
        {!readOnly && addressUnitConflict(fields) && <p role="alert" className="text-sm mt-2" style={{ color: "var(--fb-error)" }}>
          The street and Apt / Unit contain conflicting unit numbers. Please correct them before saving.
        </p>}
      </div>

      <div className="grid grid-cols-5 gap-3">
        <div className="col-span-2">
          <label className={labelClass} style={labelStyle}>City{!readOnly && requiredMark}</label>
          <input data-testid="address-city" value={fields.city}
            onChange={(e) => handleChange("city", e.target.value)} onBlur={handleBlur}
            readOnly={readOnly} placeholder="Pullman" className={inputClass} style={inputStyle(readOnly)} />
        </div>
        <div className="col-span-1">
          <label className={labelClass} style={labelStyle}>State{!readOnly && requiredMark}</label>
          <input data-testid="address-state" value={fields.state}
            onChange={(e) => handleChange("state", e.target.value.toUpperCase().slice(0, 2))} onBlur={handleBlur}
            readOnly={readOnly} placeholder="WA" maxLength={2} className={inputClass} style={inputStyle(readOnly)} />
        </div>
        <div className="col-span-2">
          <label className={labelClass} style={labelStyle}>Zip{!readOnly && requiredMark}</label>
          <input data-testid="address-zip" value={fields.zip}
            onChange={(e) => handleChange("zip", e.target.value.replace(/\D/g, "").slice(0, 5))} onBlur={handleBlur}
            readOnly={readOnly} placeholder="99163" maxLength={5}
            className={`${inputClass} tabular-nums`} style={inputStyle(readOnly)} />
        </div>
      </div>

      {!readOnly && googleActive && (
        <div data-testid="address-check-panel" data-address-guard className="space-y-1.5">
          <button type="button" data-testid="address-check-btn" onClick={runGoogleCheck}
            disabled={checking || !readyToCheck(fields) || exhausted}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold disabled:opacity-50"
            style={{ border: "1px solid var(--fb-primary)", color: "var(--fb-primary)" }}>
            {checking ? "Checking address…" : "Check address"}
          </button>
          <p data-testid="address-check-notice" className="text-xs" style={muted}>
            Checking sends this street address, unit, city, state and ZIP to Google. Names, email and date of birth are not sent.
            {exhausted ? " The daily check limit has been reached." : ""}
          </p>
        </div>
      )}

      {!readOnly && (
        <div data-testid="usps-feedback" data-address-guard aria-live="polite">
          {result?.mode === "usps" && result.status === "loading" && (
            <div className="flex items-center gap-1.5 text-xs" style={muted}>
              <svg className="animate-spin h-3 w-3 shrink-0" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Verifying with USPS…
            </div>
          )}

          {result?.status === "suggestion" && suggestion && (
            <div data-testid="usps-suggestion" className="rounded-lg px-3.5 py-2.5 text-sm flex flex-col gap-2"
              style={{ background: "#EFF6FF", border: "1px solid #BFDBFE" }}>
              <div>
                <span className="font-medium" style={{ color: "#1E40AF" }}>USPS suggests: </span>
                <span style={{ color: "#1E3A8A" }}>{formatSuggestion(suggestion)}</span>
              </div>
              <div className="flex gap-2">
                <button type="button" data-testid="usps-accept" onClick={acceptSuggestion}
                  className="px-3 py-1 rounded text-xs font-semibold transition-colors"
                  style={{ background: "#2563EB", color: "#fff" }}>Use this address</button>
                <button type="button" data-testid="usps-dismiss" onClick={dismissSuggestion}
                  className="px-3 py-1 rounded text-xs font-medium transition-colors"
                  style={{ background: "#DBEAFE", color: "#1D4ED8" }}>Keep what I typed</button>
              </div>
            </div>
          )}

          {result?.status === "not_found" && (
            <p data-testid="usps-not-found" className="text-xs" style={muted}>
              {result.message || "Address could not be confirmed by USPS — you can still save it as entered."}
            </p>
          )}

          {allowGoogleCheck && result?.mode === "google" && (
            <div data-testid="address-check-result" data-status={result.status}
              className="rounded-lg px-3.5 py-2.5 text-sm flex flex-col gap-2"
              style={{ background: "#F6F7F4", border: "1px solid var(--fb-border)" }}>
              {result.status === "checked" && (
                <p>Google found this address{result.verified ? " (Google verified)" : ""}. This is not a guarantee of delivery or residency.</p>
              )}
              {result.status === "needs_review" && (
                <>
                  <p>{result.message || "This address needs review."}</p>
                  {suggestion && (
                    <p data-testid="address-check-suggestion">
                      <span className="font-medium">Suggested: </span>{formatSuggestion(suggestion)}
                    </p>
                  )}
                  {suggestion && !canUse && (
                    <p className="text-xs" style={muted}>The suggestion is not fully confirmed, so it cannot be applied automatically.</p>
                  )}
                  {suggestion && (
                    <div className="flex gap-2">
                      {canUse && <button type="button" data-testid="usps-accept" onClick={acceptSuggestion}
                        className="px-3 py-1 rounded text-xs font-semibold" style={{ background: "#2563EB", color: "#fff" }}>
                        Use this address
                      </button>}
                      <button type="button" data-testid="usps-dismiss" onClick={dismissSuggestion}
                        className="px-3 py-1 rounded text-xs font-medium" style={{ background: "#DBEAFE", color: "#1D4ED8" }}>
                        Keep what I typed
                      </button>
                    </div>
                  )}
                </>
              )}
              {result.status === "not_checked" && <p>{result.message || "The address was not checked."} You can still save it as entered.</p>}
              {result.status === "error" && <p>{result.message}</p>}
              {result.status === "updated" && <p>{result.verified ? "Address updated. Google verified." : "Address updated. Run the check again to confirm the new address."}</p>}
              {result.status === "kept" && <p>Keeping the address as typed. It has not been confirmed.</p>}
            </div>
          )}

          {googleActive && (
            <p className="text-xs mt-1.5" style={muted} data-testid="address-check-attribution">
              Address checks by{" "}
              <a href="https://cloud.google.com/maps-platform/terms" target="_blank" rel="noopener noreferrer"
                translate="no" style={{ whiteSpace: "nowrap", fontWeight: 400, color: "#5E5E5E", fontSize: 12 }} className="underline">Google Maps</a>
              {" · "}
              <a href="/address-check-policy" target="_blank" rel="noopener noreferrer" className="underline">Privacy and terms</a>
            </p>
          )}
          {configError && <p className="text-xs" style={muted} role="status">
            Address checking is unavailable. This address has not been checked; you can still save it.
          </p>}
        </div>
      )}
    </div>
  );
}
